import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type {
  CreateFunderFromContactDto,
  CreateResidentFundingDto,
  Funder,
  ResidentFunding,
  UpdateResidentFundingDto
} from '../types/invoicingRates'
import {
  FUNDERS_FROM_CONTACT_QUERY_KEY,
  FUNDERS_QUERY_KEY,
  FUNDERS_URL,
  RESIDENT_FUNDING_CREATE_QUERY_KEY,
  RESIDENT_FUNDING_DELETE_QUERY_KEY,
  RESIDENT_FUNDING_QUERY_KEY,
  RESIDENT_FUNDING_UPDATE_QUERY_KEY,
  RESIDENT_FUNDING_URL
} from './queryConstants'

export const useGetResidentFunding = (residentId: string) =>
  useQuery<ResidentFunding[]>(
    [RESIDENT_FUNDING_QUERY_KEY, residentId],
    () =>
      getJSON({
        url: `${RESIDENT_FUNDING_URL}?residentId=${residentId}`,
        isRota: true
      }),
    { enabled: !!residentId }
  )

export const useCreateResidentFunding = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: createResidentFunding, isLoading } = useMutation<
    ResidentFunding,
    unknown,
    CreateResidentFundingDto,
    unknown
  >(
    [RESIDENT_FUNDING_CREATE_QUERY_KEY],
    (dto) =>
      postJSON({
        url: RESIDENT_FUNDING_URL,
        method: 'POST',
        json: dto,
        isRota: true
      }),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries([
          RESIDENT_FUNDING_QUERY_KEY,
          vars.residentId
        ])
      }
    }
  )

  return { createResidentFunding, isLoading }
}

export const useUpdateResidentFunding = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateResidentFunding, isLoading } = useMutation<
    ResidentFunding,
    unknown,
    { id: string; dto: UpdateResidentFundingDto; residentId: string },
    unknown
  >(
    [RESIDENT_FUNDING_UPDATE_QUERY_KEY],
    ({ id, dto }) =>
      postJSON({
        url: `${RESIDENT_FUNDING_URL}/${id}`,
        method: 'PUT',
        json: dto,
        isRota: true
      }),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries([
          RESIDENT_FUNDING_QUERY_KEY,
          vars.residentId
        ])
      }
    }
  )

  return { updateResidentFunding, isLoading }
}

export const useDeleteResidentFunding = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteResidentFunding, isLoading } = useMutation<
    void,
    unknown,
    { id: string; residentId: string },
    unknown
  >(
    [RESIDENT_FUNDING_DELETE_QUERY_KEY],
    ({ id }) =>
      postJSON({
        url: `${RESIDENT_FUNDING_URL}/${id}`,
        method: 'DELETE',
        isRota: true
      }),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries([
          RESIDENT_FUNDING_QUERY_KEY,
          vars.residentId
        ])
      }
    }
  )

  return { deleteResidentFunding, isLoading }
}

export const useCreateFunderFromContact = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: createFunderFromContact, isLoading } = useMutation<
    Funder,
    unknown,
    CreateFunderFromContactDto,
    unknown
  >(
    [FUNDERS_FROM_CONTACT_QUERY_KEY],
    (dto) =>
      postJSON({
        url: `${FUNDERS_URL}/from-contact`,
        method: 'POST',
        json: dto,
        isRota: true
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([FUNDERS_QUERY_KEY])
      }
    }
  )

  return { createFunderFromContact, isLoading }
}
